import { useEffect, useState } from 'react';

// THREE NUMBERS SOMEBODY IS TYPING INTO, which is not the same thing as three numbers.
//
// Every triple on this page is a LIVE READING AND AN INSTRUCTION at once: it follows the part
// while the part is dragged or scrubbed, because that is where the part is — and it stops
// following the moment somebody types, because a half-typed number is not a position anybody
// meant. Leaving the field is the commit and Escape puts back what was there.
//
// That behaviour was written once for the card's panel and is now wanted by the movement
// bar's four rows as well. Written twice it would be two answers to "when does a typed number
// take effect", drifting the day either is touched.
//
// TEXT AND NOT NUMBER INPUTS, deliberately: a number spinner applies as you touch it, and
// these fields feed a store write and a re-render of a 3D scene. A comma is a decimal point,
// because the page is read in French as often as in English.
export function useEditableTriple(
  // Live, in the units shown. Rounding belongs to the caller — it is what decides how many
  // decimals the reading is worth, and this hook must show exactly what is being compared.
  values: number[],
  // What was typed, in those same units. Called on blur and on Enter, never per keystroke.
  onCommit: (next: [number, number, number]) => void,
  // Changes when the fields are about a different THING — another part, another sub-step —
  // at which point whatever was half-typed about the last one goes with it.
  about?: string,
) {
  const [text, setText] = useState<string[]>(() => values.map(String));
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    if (edited) return;
    setText(values.map(String));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values[0], values[1], values[2], edited]);

  useEffect(() => { setEdited(false); }, [about]);

  const commit = (next: string[]) => {
    onCommit(next.map((v) => Number(v.replace(',', '.')) || 0) as [number, number, number]);
    setEdited(false);
  };

  return {
    text,
    at: (i: number, v: string) => {
      setEdited(true);
      setText(text.map((was, k) => (k === i ? v : was)));
    },
    commit,
    // Back to what the part actually says, discarding the edit — the way out of a number you
    // have started and thought better of, which matters precisely because leaving a field
    // commits it.
    revert: () => { setEdited(false); setText(values.map(String)); },
  };
}
